import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSlideCopy } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import type { SlideProps } from '@/components/SlideWizard'
import { copy } from './Slide6TimeToReact.copy'

const ease = [0.16, 1, 0.3, 1] as const

function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? '')
}

export default function Slide6TimeToReact(_props: SlideProps) {
  const c = useSlideCopy(copy, {
    skipSpellingFor: ['alertAmount', 'alertRetailer', 'thresholdLow', 'thresholdHigh'],
  })
  const { isActive } = _props
  const isAr = useLocale() === 'ar'
  // The slideover enters from the end edge, which is the left under RTL.
  const offscreen = isAr ? '-100%' : '100%'
  const [guideOpen, setGuideOpen] = useState(false)

  return (
    <div className="flex flex-col items-center px-4 max-w-5xl mx-auto w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.05, ease }}
        className="mb-8 w-full max-w-[880px]"
      >
        <div className="relative h-[188px] overflow-hidden rounded-[30px] bg-white">
          <div className="absolute left-1/2 top-[10px] h-[228px] w-[292px] -translate-x-1/2 overflow-hidden rounded-[42px] bg-[#10182b] p-[4px] shadow-[0_18px_40px_rgba(15,23,42,0.14)] ring-1 ring-slate-900/10">
            <div className="relative h-full rounded-[34px] bg-[#f4f5f7] ring-1 ring-white/80">
              <div className="absolute inset-x-0 top-0 flex items-center justify-between px-4 pt-2 text-[8px] font-medium text-slate-500">
                <span>9:41</span>
                <div className="flex items-center gap-1">
                  <svg className="h-[7px] w-[10px]" viewBox="0 0 10 7" fill="none" aria-hidden="true">
                    <rect x="0.75" y="1" width="6.2" height="5" rx="1" stroke="currentColor" strokeWidth="0.9" />
                    <path d="M7.6 2.4V4.6" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
                    <rect x="1.7" y="1.95" width="4.1" height="3.1" rx="0.55" fill="currentColor" />
                  </svg>
                  <svg className="h-[7px] w-[8px]" viewBox="0 0 8 7" fill="currentColor" aria-hidden="true">
                    <rect x="0.5" y="4.5" width="1.1" height="2" rx="0.35" />
                    <rect x="2.7" y="3.3" width="1.1" height="3.2" rx="0.35" />
                    <rect x="4.9" y="2.1" width="1.1" height="4.4" rx="0.35" />
                  </svg>
                </div>
              </div>

              <div className="absolute inset-x-0 top-0 flex justify-center pt-2.5">
                <div className="h-[8px] w-[78px] rounded-full bg-[#0f172a]" />
              </div>

              <div className="px-4 pt-10">
                <div className="space-y-3">
                  {[82, 64, 76].map((width, index) => (
                    <div
                      key={width}
                      className="h-2.5 rounded-full bg-slate-200/80"
                      style={{
                        width: `${width}%`,
                        opacity: 1 - index * 0.14,
                      }}
                    />
                  ))}
                </div>
                <div className="mt-5 space-y-2.5">
                  {[58, 88, 72].map((width, index) => (
                    <div
                      key={width}
                      className="h-2 rounded-full bg-slate-200/75"
                      style={{
                        width: `${width}%`,
                        opacity: 1 - index * 0.12,
                      }}
                    />
                  ))}
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: -54 }}
                animate={isActive ? { opacity: 1, y: 14 } : { opacity: 0, y: -54 }}
                transition={{ duration: 0.7, delay: 0.55, ease }}
                className="absolute inset-x-3 top-0 rounded-[24px] bg-white/96 px-4.5 py-3.5 shadow-[0_14px_28px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/80 backdrop-blur-sm"
              >
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                    <svg className="h-4.5 w-4.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                  </span>
                  <div className="min-w-0 text-start">
                    <div className="flex items-center gap-2">
                        <p className="text-[14px] font-semibold text-slate-800">
                        {c.alertTitle}
                      </p>
                      <span className="text-[13px] text-slate-400">
                        {c.alertNow}
                      </span>
                    </div>
                      <p className="mt-1.5 text-[14px] leading-snug text-slate-600">
                      {fill(c.alertBody, { amount: c.alertAmount, retailer: c.alertRetailer })}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease }}
        className="text-4xl sm:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.1] text-balance text-center max-w-3xl"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.45, ease }}
        className="mt-6 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-2xl text-center"
      >
        {c.body}
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.8, ease }}
        onClick={() => setGuideOpen(true)}
        className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 cursor-pointer"
      >
        {c.guideButton}
        <svg className="dir-flip h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
      </motion.button>

      {/* Slideover */}
      <AnimatePresence>
        {guideOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setGuideOpen(false)}
              className="fixed inset-0 z-40 bg-slate-900/40"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 w-full max-w-md overflow-y-auto border-s border-slate-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.12)]"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/90 px-6 py-4 backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                  </span>
                  <h3 className="text-lg font-semibold tracking-tight text-slate-900">
                    {c.guideTitle}
                  </h3>
                </div>
                <button
                  onClick={() => setGuideOpen(false)}
                  aria-label={c.closeAria}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-7 sm:px-8">
                <div className="space-y-7">
                  <section className="pb-1">
                    <h4 className="text-lg font-semibold text-slate-900">
                      {c.appHeading}
                    </h4>
                    <p className="mt-2 text-base leading-relaxed text-slate-600">
                      {c.appBody}
                    </p>
                  </section>

                  <section className="pb-1">
                    <h4 className="text-lg font-semibold text-slate-900">
                      {c.thresholdHeading}
                    </h4>
                    <p className="mt-2 text-base leading-relaxed text-slate-600">
                      {fill(c.thresholdBody, { low: c.thresholdLow, high: c.thresholdHigh })}
                    </p>
                  </section>

                  <section className="pb-1">
                    <h4 className="text-lg font-semibold text-slate-900">
                      {c.creditHeading}
                    </h4>
                    <p className="mt-2 text-base leading-relaxed text-slate-600">
                      {fill(c.creditBody, { account: c.primaryAccountLabel })}
                    </p>
                  </section>

                  <section>
                    <h4 className="text-lg font-semibold text-slate-900">
                      {c.spotHeading}
                    </h4>
                    <p className="mt-2 text-base leading-relaxed text-slate-600">
                      {c.spotBody}
                    </p>
                  </section>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
